import * as React from 'react';
import { Helmet } from 'react-helmet';
import { Fabric, PrimaryButton } from '@fluentui/react';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { useSelector } from 'react-redux';
import { authSelector } from 'store/auth/selector';
import { useBetreuer } from 'api/betreuer';
import { useKundeList } from 'api/kunde';
import { SelectOption } from 'formComponents/Select/types';
import classnames from 'classnames';
import { viewStyles } from 'styles/viewStyles';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { Select } from 'formComponents/Select/Select';
import { ValueType } from 'react-select';
import { Angebot, AngebotFilter, useAngebotList } from 'api/angebot';
import { AngebotItem } from 'components/Angebot/AngebotItem';
import useDebouncedState from 'hooks/useDebouncedState';
import { AngebotPosition, AngebotPositionFilter, useAngebotPositionList } from 'api/angebotPosition';
import { DebouncedSearchBox } from 'formComponents/DebouncedSearchBox';
import { AngebotEdit, AngebotEditData } from 'components/Angebot/AngebotEdit';
import { List } from '@fluentui/react/lib/List';
import { useHistory, useParams } from 'react-router-dom';
import AutoSizer from 'react-virtualized-auto-sizer';
import moment from 'moment-timezone';
import { FormDatePicker } from 'formComponents/FormDatePicker';
import { lookupsLoadingFinishedSelector } from 'store/lookups/selector';
import usePersistentState from 'hooks/usePersistentState';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { formStyles } from 'styles/formStyles';
import { RecordViewUrlParams } from 'layout/App/types';
import { createRecordUrl, decodeRecordNr } from 'utils/formatter';
import { AngebotPositionenHeader } from 'components/Angebot/AngebotPositionenHeader';
import { useTerminList } from 'api/termin';
import { AngebotStatusMulti } from 'formComponents/lookups/AngebotStatusMulti';

const defaultMaxRows = 250;

const styles = mergeStyleSets({
    searchBar1: {
        display: 'grid',
        gridTemplateColumns: '200px 150px 150px 180px 1fr 1fr max-content max-content',
        gridColumnGap: 10,
        marginTop: 10,
    },
    searchBar2: {
        display: 'grid',
        gridTemplateColumns: '170px 170px 170px 170px',
        gridColumnGap: 10,
        marginTop: 10,
    },
});

const alleBetreuerOption = { value: '__all__', label: 'Alle Betreuer' };

interface FilterValues {
    maxRows: number;
    kNr?: number;
    nr?: string;
    auNr?: string;
    status: string;
    statusList: number[];
    betreuer: SelectOption;
    datumVon?: string;
    datumBis?: string;
    datBisVon?: string;
    datBisBis?: string;
    searchText: string;
}

const defaultFilterValues: FilterValues = {
    maxRows: defaultMaxRows,
    kNr: undefined,
    nr: undefined,
    auNr: '',
    status: '__all__',
    statusList: [],
    betreuer: alleBetreuerOption,
    datumVon: undefined,
    datumBis: undefined,
    datBisVon: undefined,
    datBisBis: undefined,
    searchText: '',
};

export const AngebotView = (): JSX.Element => {
    const auth = useSelector(authSelector);
    const history = useHistory();
    const lookupsLoadingFinished = useSelector(lookupsLoadingFinishedSelector);
    const betreuer = useBetreuer();
    const [viewMode, setViewMode] = usePersistentState<'short' | 'long'>('long', 'AngebotView.viewMode');

    const { urlKundeNr, urlRecordNr } = useParams<RecordViewUrlParams>();
    const kNr = urlKundeNr ? parseInt(urlKundeNr, 10) : undefined;

    // const isSmallScreenEmbedded = useMediaQuery({ query: `(max-width: 1800px)` });
    // const isSmallScreenNotEmbedded = useMediaQuery({ query: `(max-width: 1600px)` });
    // const isSmallScreen = urlKundeNr ? isSmallScreenEmbedded : isSmallScreenNotEmbedded;

    const angebotEditData = React.useMemo<AngebotEditData | null>(() => {
        if (urlRecordNr) {
            return {
                aNr: decodeRecordNr(urlRecordNr),
                kNr,
            };
        }
        return null;
    }, [kNr, urlRecordNr]);

    const kundeList = useKundeList({ nr: kNr || -1 });

    const betreuerFilterOptions = React.useMemo<SelectOption[]>(() => {
        const options: SelectOption[] = [];

        if (auth.rolle === 'AD') {
            options.push({
                value: auth.nr?.toString() || '',
                label: auth.name || '?',
            });
        } else {
            options.push(alleBetreuerOption);
            betreuer
                .filter((b) => b.aktiv && b.name)
                .sort((a, b) => a.name.localeCompare(b.name))
                .forEach((b) => options.push({ value: b.nr.toString(), label: b.name }));
        }

        return options;
    }, [auth, betreuer]);

    const [filterValues, debouncedFilterValues, setFilterValues] = useDebouncedState<FilterValues>(() => {
        if (auth.rolle === 'AD') {
            return {
                ...defaultFilterValues,
                betreuer: {
                    value: auth.kz || '',
                    label: auth.name || '?',
                },
            };
        }
        return {
            ...defaultFilterValues,
            betreuer: alleBetreuerOption,
        };
    }, 1000);

    const filter = React.useMemo<AngebotFilter>(() => {
        return {
            kNr,
            betreuer:
                debouncedFilterValues.betreuer.value === '__all__'
                    ? undefined
                    : parseInt(debouncedFilterValues.betreuer.value, 10),
            // status: debouncedFilterValues.status === '__all__' ? undefined :
            // parseInt(debouncedFilterValues.status, 10),
            statusList: debouncedFilterValues.statusList,
            nrSearch: debouncedFilterValues.nr,
            auNrSearch: debouncedFilterValues.auNr,
            datumVon: debouncedFilterValues.datumVon,
            datumBis: debouncedFilterValues.datumBis,
            datBisVon: debouncedFilterValues.datBisVon,
            datBisBis: debouncedFilterValues.datBisBis,
            searchText: debouncedFilterValues.searchText,
            withKunde: true,
            maxRows: defaultMaxRows,
        };
    }, [kNr, debouncedFilterValues]);

    const angebotList = useAngebotList(filter);

    const positionenFilter = React.useMemo<AngebotPositionFilter>(() => {
        if (angebotList.isLoading || angebotList.isLoading || angebotList.isError) {
            return {};
        }

        return {
            aNrList: angebotList.data?.map((angebot) => angebot.nr) || [],
        };
    }, [angebotList]);

    const angebotPositionList = useAngebotPositionList(positionenFilter, { enabled: false });

    React.useEffect(() => {
        if (positionenFilter.aNrList) {
            angebotPositionList.refetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [positionenFilter]);

    const angebotPositionenByANr = React.useMemo<{ [key: string]: AngebotPosition[] }>(() => {
        if (
            angebotPositionList.isLoading ||
            angebotPositionList.isError ||
            (angebotPositionList.data?.length || 0) === 0
        ) {
            return {};
        }

        const newAngebotPositionenByANr: { [key: string]: AngebotPosition[] } = {};

        angebotPositionList.data?.forEach((angebotPosition) => {
            if (!newAngebotPositionenByANr[angebotPosition.aNr]) {
                newAngebotPositionenByANr[angebotPosition.aNr] = [];
            }

            newAngebotPositionenByANr[angebotPosition.aNr].push(angebotPosition);
        });

        return newAngebotPositionenByANr;
    }, [angebotPositionList]);

    const kNrArtikelGruppeNrAndArtikelNrList = React.useMemo(() => {
        const list: string[] = [];

        (angebotList.data || []).forEach((angebot) => {
            const positionen = angebotPositionenByANr[angebot.nr];
            if (positionen) {
                positionen.forEach((p) => {
                    if (p.artikelGruppeNr && p.artikelNr) {
                        list.push(angebot.kNr + '|' + p.artikelGruppeNr + '|' + p.artikelNr);
                    }
                });
            }
        });

        return list;
    }, [angebotList.data, angebotPositionenByANr]);

    const wiedervorlagen = useTerminList({
        terminStatus: 0,
        terminArt: 'W',
        kNrArtikelGruppeNrAndArtikelNrList,
    });

    const onRenderCell = React.useCallback(
        (angebot: Angebot | undefined): JSX.Element => {
            if (!angebot) {
                return <div />;
            }

            return (
                <AngebotItem
                    key={angebot.nr}
                    angebot={angebot}
                    withKunde={!urlKundeNr}
                    viewMode={viewMode}
                    withHeader={false}
                    angebotPositionen={angebotPositionenByANr[angebot.nr] || []}
                    wiedervorlagen={wiedervorlagen.data || []}
                    editAngebot={() => history.push(createRecordUrl('angebote', urlKundeNr, angebot.nr))}
                    refetch={() => {
                        wiedervorlagen.refetch().then();
                        angebotPositionList.refetch().then(() => {
                            angebotList.refetch().then();
                        });
                    }}
                />
            );
        },
        [urlKundeNr, viewMode, angebotPositionenByANr, wiedervorlagen, history, angebotPositionList, angebotList],
    );

    const renderDatumVon = () => (
        <FormDatePicker
            placeholder="Datum (von)"
            value={filterValues.datumVon}
            onChange={(newValue) => {
                setFilterValues({
                    ...filterValues,
                    datumVon: moment(newValue).format('YYYY-MM-DD') + 'T00:00:00',
                });
            }}
        />
    );

    const renderDatumBis = () => (
        <FormDatePicker
            placeholder="Datum (bis)"
            value={filterValues.datumBis}
            onChange={(newValue) => {
                setFilterValues({
                    ...filterValues,
                    datumBis: moment(newValue).format('YYYY-MM-DD') + 'T00:00:00',
                });
            }}
        />
    );

    const renderDatBisVon = () => (
        <FormDatePicker
            placeholder="Gültig (von)"
            value={filterValues.datBisVon}
            onChange={(newValue) => {
                setFilterValues({
                    ...filterValues,
                    datBisVon: moment(newValue).format('YYYY-MM-DD') + 'T00:00:00',
                });
            }}
        />
    );

    const renderDatBisBis = () => (
        <FormDatePicker
            placeholder="Gültig (bis)"
            value={filterValues.datBisBis}
            onChange={(newValue) => {
                setFilterValues({
                    ...filterValues,
                    datBisBis: moment(newValue).format('YYYY-MM-DD') + 'T00:00:00',
                });
            }}
        />
    );

    if (auth.rolle === 'AD' && kundeList.data?.length === 1) {
        if (kundeList.data[0].betreuer && kundeList.data[0].betreuer !== auth.nr) {
            return (
                <Fabric className={viewStyles.container}>
                    <div className={viewStyles.listContainer}>
                        <div className={viewStyles.noData}>Keine Daten.</div>
                    </div>
                </Fabric>
            );
        }
    }

    if (!lookupsLoadingFinished) {
        return <div />;
    }

    return (
        <Fabric
            className={classnames(viewStyles.container, viewStyles.twoSearchBarLines, {
                [viewStyles.embedded]: !!urlKundeNr,
            })}
        >
            <Helmet>
                <title>
                    {urlKundeNr ? 'Kunde ' + urlKundeNr + ' | ' : ''}
                    {angebotEditData?.aNr ? 'Angebot ' + angebotEditData?.aNr : 'Angebote'}
                </title>
            </Helmet>
            <div className={styles.searchBar1}>
                <DebouncedSearchBox
                    onChange={(newValue) => {
                        setFilterValues({
                            ...filterValues,
                            searchText: newValue === undefined ? '' : newValue,
                        });
                    }}
                />
                <DebouncedSearchBox
                    placeholder="Angebot-Nr."
                    onChange={(newValue) => {
                        setFilterValues({
                            ...filterValues,
                            nr: newValue === undefined ? '' : newValue,
                        });
                    }}
                />
                <DebouncedSearchBox
                    placeholder="Auftrag-Nr."
                    onChange={(newValue) => {
                        setFilterValues({
                            ...filterValues,
                            auNr: newValue === undefined ? '' : newValue,
                        });
                    }}
                />
                <Select
                    name="betreuer"
                    value={filterValues.betreuer}
                    options={betreuerFilterOptions}
                    onChange={(newValue: ValueType<SelectOption, false>): void => {
                        setFilterValues({
                            ...filterValues,
                            betreuer: newValue || alleBetreuerOption,
                        });
                    }}
                />
                <AngebotStatusMulti
                    value={filterValues.statusList.map((v) => v.toString())}
                    renderLabel="placeholder"
                    onChange={(newValues) => {
                        setFilterValues({
                            ...filterValues,
                            statusList: newValues.map((v) => parseInt(v, 10)),
                        });
                    }}
                />

                <div />

                <div>
                    <Checkbox
                        className={formStyles.checkboxInsideForm}
                        label="Kurzansicht"
                        checked={viewMode === 'short'}
                        onChange={(event, checked) => {
                            setViewMode(checked ? 'short' : 'long');
                        }}
                    />
                </div>

                {auth.rolle !== 'AD' && auth.rolle !== 'EX' && (
                    <PrimaryButton
                        onClick={() => history.push(createRecordUrl('angebote', urlKundeNr, 'neu'))}
                        text="Neues Angebot"
                    />
                )}
            </div>

            <div className={styles.searchBar2}>
                {renderDatumVon()}
                {renderDatumBis()}
                {renderDatBisVon()}
                {renderDatBisBis()}
            </div>

            {angebotList.isLoading || angebotList.isFetching ? (
                <LoadingIndicator />
            ) : (
                <div className={viewStyles.listContainer}>
                    {angebotList?.data && angebotList?.data.length === 0 ? (
                        <div className={viewStyles.noData}>Keine Angebote.</div>
                    ) : (
                        <>
                            {viewMode === 'short' && (
                                <div className={viewStyles.listContainer}>
                                    <table className={formStyles.stickyPositionenHeaderTable}>
                                        <AngebotPositionenHeader />
                                    </table>
                                </div>
                            )}
                            <AutoSizer>
                                {({ height, width }) => (
                                    <div
                                        style={{ width, height: height - 25, overflowX: 'hidden', overflowY: 'scroll' }}
                                    >
                                        <List
                                            key={
                                                viewMode +
                                                (angebotPositionList.isSuccess ? 1 : 0) +
                                                (wiedervorlagen.isSuccess ? 1 : 0)
                                            }
                                            items={angebotList.data || []}
                                            onRenderCell={onRenderCell}
                                        />
                                    </div>
                                )}
                            </AutoSizer>
                        </>
                    )}
                </div>
            )}
            {angebotEditData !== null && (
                <AngebotEdit
                    defaultKNr={angebotEditData.kNr}
                    aNr={angebotEditData.aNr}
                    onClose={(refetch) => {
                        history.push(createRecordUrl('angebote', urlKundeNr));
                        if (refetch) {
                            wiedervorlagen.refetch().then();
                            angebotPositionList.refetch().then(() => {
                                angebotList.refetch().then();
                            });
                        }
                    }}
                    onReopen={(newANr: string) => {
                        history.push(createRecordUrl('angebote', urlKundeNr));
                        angebotPositionList.refetch().then(() => {
                            angebotList.refetch().then(() => {
                                history.push(createRecordUrl('angebote', angebotEditData.kNr?.toString(), newANr));
                            });
                        });
                    }}
                />
            )}
        </Fabric>
    );
};
